import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Paper,
  Avatar,
  Group,
  Stack,
  TextInput,
  Select,
  MultiSelect,
  Modal,
  Badge,
  SimpleGrid,
  ActionIcon,
  Slider,
  Box,
  FileButton,
  Center,
  Alert
} from '@mantine/core';
import { preferencesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddPrefs, setShowAddPrefs] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [profileUpdate, setProfileUpdate] = useState({
    name: '',
    birthdate: '',
    profile_picture_url: '',
    gender: '',
    traits: [],
    occupations: [],
    activities: [],
    hobbies: [],
    styles: []
  });

  const [prefForm, setPrefForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0
  });

  const categories = [
    'Technology', 'Food & Drink', 'Music', 'Sports & Fitness',
    'Books & Reading', 'Movies & TV', 'Gaming', 'Fashion & Style',
    'Travel & Adventure', 'Home & Decor', 'Pets & Animals', 'Art & Crafts',
    'Cooking & Culinary', 'Photography', 'Outdoors & Nature', 'Wellness & Health'
  ];

  const genderOptions = [
    'Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other'
  ];

  const traitOptions = [
    'Adventurous', 'Analytical', 'Artistic', 'Athletic', 'Calm',
    'Caring', 'Confident', 'Creative', 'Curious', 'Detail-oriented',
    'Energetic', 'Friendly', 'Funny', 'Generous', 'Organized',
    'Outgoing', 'Patient', 'Practical', 'Quiet', 'Spontaneous'
  ];

  const occupationOptions = [
    'Student', 'Teacher', 'Engineer', 'Doctor', 'Nurse',
    'Artist', 'Designer', 'Developer', 'Manager', 'Entrepreneur',
    'Scientist', 'Writer', 'Musician', 'Chef', 'Athlete',
    'Consultant', 'Analyst', 'Researcher', 'Retired', 'Other'
  ];

  const activityOptions = [
    'Reading', 'Writing', 'Running', 'Cycling', 'Swimming',
    'Hiking', 'Cooking', 'Baking', 'Gardening', 'Traveling',
    'Gaming', 'Coding', 'Painting', 'Drawing', 'Photography',
    'Yoga', 'Meditation', 'Dancing', 'Singing', 'Volunteering'
  ];

  const hobbyOptions = [
    'Collecting', 'Crafting', 'DIY Projects', 'Model Building', 'Knitting',
    'Woodworking', 'Pottery', 'Origami', 'Calligraphy', 'Scrapbooking',
    'Bird Watching', 'Astronomy', 'Genealogy', 'Coin Collecting', 'Stamp Collecting',
    'Board Games', 'Video Games', 'Puzzles', 'Magic Tricks', 'Juggling'
  ];

  const styleOptions = [
    'Modern', 'Classic', 'Minimalist', 'Bohemian', 'Vintage',
    'Industrial', 'Rustic', 'Scandinavian', 'Mid-Century', 'Contemporary',
    'Traditional', 'Eclectic', 'Art Deco', 'Farmhouse', 'Coastal'
  ];

  useEffect(() => {
    loadUserPreferences();

    if (user) {
      setProfileUpdate({
        name: user.name || '',
        birthdate: user.birthdate ? new Date(user.birthdate).toISOString().split('T')[0] : '',
        profile_picture_url: user.profile_picture_url || '',
        gender: user.gender || '',
        traits: user.traits || [],
        occupations: user.occupations || [],
        activities: user.activities || [],
        hobbies: user.hobbies || [],
        styles: user.styles || []
      });
      setLoading(false);
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const result = await updateProfile({
        name: profileUpdate.name,
        birthdate: profileUpdate.birthdate || null,
        profile_picture_url: profileUpdate.profile_picture_url || null,
        gender: profileUpdate.gender,
        traits: profileUpdate.traits,
        occupations: profileUpdate.occupations,
        activities: profileUpdate.activities,
        hobbies: profileUpdate.hobbies,
        styles: profileUpdate.styles
      });

      if (result.success) {
        setShowEditProfile(false);
        alert('Profile updated successfully!');
      } else {
        alert('Failed to update profile: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with specified quality
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setProfileUpdate({
      ...profileUpdate,
      profile_picture_url: previewUrl
    });

    try {
      // Compress the image to maintain quality while reducing file size
      const compressedBlob = await compressImage(file, 800, 800, 0.85);

      // Convert compressed image to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedBlob);
      });

      const result = await updateProfile({
        profile_picture_url: base64Data
      });

      if (!result.success) {
        setProfileUpdate({
          ...profileUpdate,
          profile_picture_url: user?.profile_picture_url || ''
        });
        alert('Failed to upload profile picture: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      setProfileUpdate({
        ...profileUpdate,
        profile_picture_url: user?.profile_picture_url || ''
      });
      alert('Failed to upload profile picture');
    }
  };

  const loadUserPreferences = async () => {
    try {
      const response = await preferencesAPI.getForSubject('user', 'current');
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPreference = async (e) => {
    e.preventDefault();
    if (!prefForm.tag.trim()) {
      alert('Please enter a preference or interest');
      return;
    }

    try {
      await preferencesAPI.create({
        subject_type: 'user',
        subject_id: 'current',
        category: prefForm.category,
        tag: prefForm.tag,
        weight: prefForm.weight
      });

      setPrefForm({
        category: 'Technology',
        tag: '',
        weight: 0
      });
      setShowAddPrefs(false);
      loadUserPreferences();
    } catch (error) {
      alert('Failed to add preference: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeletePreference = async (prefId) => {
    try {
      await preferencesAPI.delete(prefId);
      loadUserPreferences();
    } catch (error) {
      alert('Failed to delete preference');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 12) {
      setPasswordError('Password must be at least 12 characters long');
      return;
    }

    setSaving(true);
    try {
      await axios.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      alert('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Stack align="center" gap="md">
            <Text size="lg">Loading your profile...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(102,126,234,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Profile Header */}
        <Paper p="xl" radius="lg" withBorder mb="xl" style={{ 
          background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(0,0,0,0) 100%)',
          borderColor: 'rgba(102,126,234,0.2)'
        }}>
          <Stack align="center" gap="md">
            <Box style={{ position: 'relative' }}>
              <Avatar
                src={user?.profile_picture_url}
                size={120}
                radius="xl"
                imageProps={{ 
                  style: { 
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%'
                  } 
                }}
                style={{
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                {!user?.profile_picture_url && '👤'}
              </Avatar>
              <ActionIcon
                size="lg"
                radius="xl"
                variant="gradient"
                gradient={{ from: 'grape', to: 'pink' }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: -10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                onClick={() => setShowEditProfile(true)}
              >
                ✏️
              </ActionIcon>
            </Box>

            <Stack align="center" gap="xs">
              <Title
                order={1}
                style={{
                  background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {user?.name || 'My Persona'}
              </Title>
              <Text size="lg" c="dimmed">{user?.email}</Text>
              {user?.birthdate && (
                <Badge size="lg" variant="light" color="pink">
                  🎂 Birthday: {new Date(user.birthdate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Badge>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* About Me Section */}
        <Paper p="xl" radius="lg" withBorder mb="xl">
  <Group justify="space-between" mb="xl">
    <Box>
      <Title order={2} mb="xs">
        <Text
          component="span"
          style={{
            background: 'linear-gradient(45deg, #f59e0b 0%, #d97706 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          🎭 About Me
        </Text>
      </Title>
      <Text c="dimmed">
        Tell us about yourself to get better gift recommendations
      </Text>
    </Box>
    <Button
      variant="gradient"
      gradient={{ from: 'orange', to: 'red' }}
      onClick={() => setShowEditProfile(true)}
    >
      ✏️ Edit Persona
    </Button>
  </Group>

  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
    {/* Gender */}
    {user?.gender && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">Gender</Text>
        <Badge size="lg" variant="light" color="blue">
          {user.gender}
        </Badge>
      </Paper>
    )}

    {/* Traits */}
    {user?.traits && user.traits.length > 0 && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">Personality Traits</Text>
        <Group gap="xs">
          {user.traits.map((trait, idx) => (
            <Badge key={idx} variant="light" color="grape">
              {trait}
            </Badge>
          ))}
        </Group>
      </Paper>
    )}

    {/* Occupations */}
    {user?.occupations && user.occupations.length > 0 && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">What I Do</Text>
        <Group gap="xs">
          {user.occupations.map((occ, idx) => (
            <Badge key={idx} variant="light" color="cyan">
              💼 {occ}
            </Badge>
          ))}
        </Group>
      </Paper>
    )}

    {/* Activities */}
    {user?.activities && user.activities.length > 0 && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">My Activities</Text>
        <Group gap="xs">
          {user.activities.map((act, idx) => (
            <Badge key={idx} variant="light" color="green">
              🏃 {act}
            </Badge>
          ))}
        </Group>
      </Paper>
    )}

    {/* Hobbies */}
    {user?.hobbies && user.hobbies.length > 0 && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">My Hobbies</Text>
        <Group gap="xs">
          {user.hobbies.map((hobby, idx) => (
            <Badge key={idx} variant="light" color="pink">
              🎨 {hobby}
            </Badge>
          ))}
        </Group>
      </Paper>
    )}

    {/* Styles */}
    {user?.styles && user.styles.length > 0 && (
      <Paper p="md" withBorder radius="md">
        <Text size="sm" c="dimmed" mb="xs">My Style</Text>
        <Group gap="xs">
          {user.styles.map((style, idx) => (
            <Badge key={idx} variant="light" color="indigo">
              👔 {style}
            </Badge>
          ))}
        </Group>
      </Paper>
    )}
  </SimpleGrid>

  {/* Empty State */}
  {(!user?.gender && 
     (!user?.traits || user.traits.length === 0) &&
     (!user?.occupations || user.occupations.length === 0) &&
     (!user?.activities || user.activities.length === 0) &&
     (!user?.hobbies || user.hobbies.length === 0) &&
     (!user?.styles || user.styles.length === 0)) && (
    <Center py="xl">
      <Stack align="center" gap="md">
        <Text size="60px">🎭</Text>
        <Title order={3}>Build Your Persona!</Title>
        <Text c="dimmed" ta="center" maw={500}>
          Add information about yourself to get personalized gift recommendations.
        </Text>
        <Button
          variant="gradient"
          gradient={{ from: 'orange', to: 'red' }}
          onClick={() => setShowEditProfile(true)}
        >
          Get Started
        </Button>
      </Stack>
    </Center>
  )}
</Paper>

{/* My Preferences Section */}
<Paper p="xl" radius="lg" withBorder>
  <Group justify="space-between" mb="xl">
    <Box>
      <Title order={2} mb="xs">
        <Text
          component="span"
          style={{
            background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          🎯 My Likes & Dislikes
        </Text>
      </Title>
      <Text c="dimmed">
        What do you love and hate? This helps friends find perfect gifts for you.
      </Text>
    </Box>
    <Button
      variant="gradient"
      gradient={{ from: 'grape', to: 'pink' }}
      onClick={() => setShowAddPrefs(!showAddPrefs)}
    >
      {showAddPrefs ? 'Cancel' : '➕ Add Preference'}
    </Button>
  </Group>

  {/* Add Preference Form */}
  {showAddPrefs && (
            <Paper p="md" mb="xl" withBorder radius="md" style={{ background: 'rgba(102,126,234,0.05)' }}>
              <Title order={4} mb="md">Add a Preference</Title>
              <form onSubmit={handleAddPreference}>
                <Stack gap="md">
                  <Select
                    label="Category"
                    data={categories}
                    value={prefForm.category}
                    onChange={(value) => setPrefForm({ ...prefForm, category: value })}
                  />

                  <TextInput
                    label="What do you like/hate?"
                    placeholder="e.g., coffee, horror movies, spicy food"
                    value={prefForm.tag}
                    onChange={(e) => setPrefForm({ ...prefForm, tag: e.target.value })}
                    required
                  />

                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={500}>Preference Strength</Text>
                      <Badge
                        size="lg"
                        color={getWeightColor(prefForm.weight)}
                        variant="filled"
                      >
                        {getWeightText(prefForm.weight)}
                      </Badge>
                    </Group>
                    <Slider
                      value={prefForm.weight}
                      onChange={(value) => setPrefForm({ ...prefForm, weight: value })}
                      min={-5}
                      max={5}
                      step={1}
                      marks={[
                        { value: -5, label: '😡' },
                        { value: -3, label: '😕' },
                        { value: 0, label: '😐' },
                        { value: 3, label: '😊' },
                        { value: 5, label: '🤩' }
                      ]}
                      color={getWeightColor(prefForm.weight)}
                      styles={{ markLabel: { fontSize: '16px' } }}
                    />
                    <Group justify="space-between" mt="xs">
                      <Text size="xs" c="dimmed">Hate It</Text>
                      <Text size="xs" c="dimmed">Love It</Text>
                    </Group>
                  </Box>

                  <Button
                    type="submit"
                    variant="gradient"
                    gradient={{ from: 'teal', to: 'green' }}
                  >
                    Add Preference
                  </Button>
                </Stack>
              </form>
              
            </Paper>

          )}

          {/* Preferences Grid */}
          {preferences.length === 0 ? (
            <Center py="xl">
              <Stack align="center" gap="md">
                <Text size="60px">🤔</Text>
                <Title order={3}>No preferences yet!</Title>
                <Text c="dimmed" ta="center" maw={500}>
                  Add some likes and dislikes above to help friends find perfect gifts for you.
                </Text>
              </Stack>
            </Center>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
              {preferences.map(pref => (
                <Paper
                  key={pref.id}
                  p="md"
                  withBorder
                  radius="md"
                  style={{
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Stack gap="xs">
                    <Badge size="xs" variant="light" color="grape">
                      {pref.category}
                    </Badge>

                    <Text size="md" fw={500} lineClamp={2}>
                      {pref.tag}
                    </Text>

                    <Group justify="space-between">
                      <Badge
                        size="md"
                        color={getWeightColor(pref.weight)}
                        variant="filled"
                      >
                        {getWeightText(pref.weight)}
                      </Badge>

                      <ActionIcon
                        size="sm"
                        color="red"
                        variant="subtle"
                        onClick={() => handleDeletePreference(pref.id)}
                        title="Remove this preference"
                      >
                        ✕
                      </ActionIcon>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Paper>

        {/* Security Section */}
        <Paper p="xl" radius="lg" withBorder mt="xl">
          <Group justify="space-between" mb="md">
            <Box>
              <Title order={2} mb="xs">
                <Text
                  component="span"
                  style={{
                    background: 'linear-gradient(45deg, #ef4444 0%, #dc2626 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  🔒 Security Settings
                </Text>
              </Title>
              <Text c="dimmed">
                Manage your account security and password
              </Text>
            </Box>
          </Group>

          <Button
            variant="light"
            color="red"
            onClick={() => setShowChangePassword(true)}
          >
            🔑 Change Password
          </Button>
        </Paper>

        {/* Privacy Note */}
        <Alert mt="xl" color="blue" variant="light" radius="md">
          <Text size="sm">
            <strong>Privacy Note:</strong> Your preferences and birthday information are visible to your friends to help them choose better gifts for you. Friends can only see your approved connections' profiles.
          </Text>
        </Alert>
      </Container>

      {/* Edit Profile Modal */}
      <Modal
        opened={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        title={<Text size="xl" fw={700}>✏️ Edit Profile</Text>}
        size="md"
        centered
      >
        <form onSubmit={handleUpdateProfile}>
          <Stack gap="md">
            <TextInput
              label="Full Name"
              placeholder="Your full name"
              value={profileUpdate.name}
              onChange={(e) => setProfileUpdate({ ...profileUpdate, name: e.target.value })}
              required
            />

            <Box>
              <Text size="sm" fw={500} mb="xs">Profile Picture</Text>
              <FileButton
                onChange={handleProfilePictureUpload}
                accept="image/*"
              >
                {(props) => (
                  <Button {...props} variant="light" fullWidth>
                    📷 Upload New Picture
                  </Button>
                )}
              </FileButton>
              <Text size="xs" c="dimmed" mt="xs">
                Upload a profile picture (optional, max 5MB)
              </Text>
            </Box>

            <TextInput
              label="Birthday"
              type="date"
              value={profileUpdate.birthdate}
              onChange={(e) => setProfileUpdate({ ...profileUpdate, birthdate: e.target.value })}
              description="Your birthday for birthday groups and gift notifications (optional)"
            />
            <Select
              label="Gender"
              placeholder="Select your gender"
              data={genderOptions}
              value={profileUpdate.gender}
              onChange={(value) => setProfileUpdate({ ...profileUpdate, gender: value })}
              clearable
            />

            <MultiSelect
              label="Personality Traits"
              placeholder="Select traits that describe you"
              data={traitOptions}
              value={profileUpdate.traits}
              onChange={(values) => setProfileUpdate({ ...profileUpdate, traits: values })}
              searchable
              clearable
            />

            <MultiSelect
              label="What I Do"
              placeholder="Your occupation or role"
              data={occupationOptions}
              value={profileUpdate.occupations}
              onChange={(values) => setProfileUpdate({ ...profileUpdate, occupations: values })}
              searchable
              clearable
            />

            <MultiSelect
              label="My Activities"
              placeholder="Things you regularly do"
              data={activityOptions}
              value={profileUpdate.activities}
              onChange={(values) => setProfileUpdate({ ...profileUpdate, activities: values })}
              searchable
              clearable
            />

            <MultiSelect
              label="My Hobbies"
              placeholder="Your hobbies and interests"
              data={hobbyOptions}
              value={profileUpdate.hobbies}
              onChange={(values) => setProfileUpdate({ ...profileUpdate, hobbies: values })}
              searchable
              clearable
            />

            <MultiSelect
              label="My Style"
              placeholder="Your style preferences"
              data={styleOptions}
              value={profileUpdate.styles}
              onChange={(values) => setProfileUpdate({ ...profileUpdate, styles: values })}
              searchable
              clearable
            />
            <Group grow>
              <Button
                variant="gradient"
                gradient={{ from: 'grape', to: 'pink' }}
                type="submit"
                loading={saving}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEditProfile(false)}
                disabled={saving}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        opened={showChangePassword}
        onClose={() => {
          setShowChangePassword(false);
          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordError('');
        }}
        title={<Text size="xl" fw={700}>🔑 Change Password</Text>}
        size="md"
        centered
      >
        <form onSubmit={handleChangePassword}>
          <Stack gap="md">
            {passwordError && (
              <Alert color="red" variant="light">
                {passwordError}
              </Alert>
            )}

            <TextInput
              label="Current Password"
              type="password"
              placeholder="Enter your current password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required
            />

            <TextInput
              label="New Password"
              type="password"
              placeholder="Enter new password (min 12 characters)"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required
            />

            <TextInput
              label="Confirm New Password"
              type="password"
              placeholder="Re-enter new password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
            />

            <Group grow>
              <Button
                variant="gradient"
                gradient={{ from: 'red', to: 'orange' }}
                type="submit"
                loading={saving}
              >
                Change Password
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
};

export default ProfilePage;
